import { inject, Injectable } from '@angular/core';
import { UtilService } from '@services/util/util.service';
import { AES, enc } from 'crypto-js';
import { backboneDatabase } from '@services/perform/perform-idb/backbone.db';
@Injectable({
    providedIn: 'root',
})
export class AppSettingsService {
    private readonly utilSvc = inject(UtilService);

    constructor() {}

    /**
     * Gets a value (if existing) for a key as a String Object. A default value can be provided in case there is no existing value.
     * @param key — The key to check for.
     * @param defaultValue — An optional value to be returned in case there is no existing value.
     */
    public async getString(key: string, defaultValue?: string): Promise<string | undefined> {
        const settingObj = await backboneDatabase.appSettings.get(key);
        let value = settingObj?.value;

        if (!value) {
            if (defaultValue) {
                return defaultValue;
            } else {
                return undefined;
            }
        } else {
            const impureKey = this.utilSvc.setImpurity(key);
            const _value = AES.decrypt(value, impureKey).toString(enc.Utf8);
            return _value;
        }
    }

    /**
     * Sets a String Object for a key.
     * @param key — The key.
     * @param value — The value.
     */
    public async setString(key: string, value: string): Promise<string> {
        let _value = value;
        const impureKey = this.utilSvc.setImpurity(key);
        _value = AES.encrypt(value, impureKey).toString();
        return await backboneDatabase.appSettings.put({ key, value: _value });
    }

    public async set(key: string, value: any): Promise<void> {
        await backboneDatabase.appSettings.put({ key, value });
    }

    public async get<T>(key: string, defaultValue?: T): Promise<T | null> {
        const result = await backboneDatabase.appSettings.get(key);
        if (result) {
            return result.value;
        } else {
            if (defaultValue) {
                return defaultValue;
            }
            return null;
        }
    }

    async delete(key: string) {
        return await backboneDatabase.appSettings.where({ key }).delete();
    }
    async exists(key: string) {
        return (await backboneDatabase.appSettings.where({ key }).count()) > 0;
    }
}
